import React, { FC, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import stringifyObject from 'stringify-object';
import { overrides } from '@cld/button';
import { Code } from '@cld/code-snippet';
import CopyToClipboard from '@cld/copy-to-clipboard';
import Icon from '@cld/icon';
import { CodeTabSectionProps, CollapsibleSection, CodeWrapper, ButtonsWrapper, ExpandButton } from '@cld/widget-demos-common';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { messages } from '../../../../i18n';
import { configToWidgetOptions } from '../../../../services/widget-service';
import { useConfig, useOptions } from '../../../../selectors';
import { Config, OptionsState } from '../../../../types';

const { Button } = overrides.mediaLibraryWidgetDemoOverrides;

const getEmbedCode = (config: Config, options: OptionsState) => {
  const updateOptions = stringifyObject(configToWidgetOptions(config, options), {
    indent: '  ',
    singleQuotes: true,
  });
  return `const myEditor = cloudinary.mediaEditor();
myEditor.update(${updateOptions});
myEditor.show();
myEditor.on('export', function(data) {
  console.log(data);
});`;
};

export const EmbedCode: FC<CodeTabSectionProps> = ({ isExpanded, onExpandToggle }) => {
  const analytics = useAnalytics();
  const { formatMessage } = useIntl();
  const config = useConfig();
  const options = useOptions();
  const embedCode = getEmbedCode(config, options);

  const handleExpandButtonClick = useCallback(() => {
    const action = !isExpanded ? 'expand' : 'collapse';
    analytics(action, `${action} embed code`);
    onExpandToggle();
  }, [isExpanded, analytics, onExpandToggle]);

  const handleCopyEmbedCode = useCallback(() => {
    analytics('copy to clipboard', 'copy media editor configuration to clipboard');
  }, [analytics]);

  return (
    <CollapsibleSection title={formatMessage(messages['section.embedCode'])}>
      <CodeWrapper size="full">
        <Code syntax="javascript" showLineNumbers data-test="embed-code">
          {embedCode}
        </Code>
      </CodeWrapper>
      <ButtonsWrapper>
        <CopyToClipboard tooltipAfterText="Copied" copiedValue={embedCode}>
          <Button size="sm" onClick={handleCopyEmbedCode} data-test="copy-code-to-clipboard">
            <Icon name="copy" />
            <FormattedMessage {...messages['section.embedCode.copyCodeToClipboard']} />
          </Button>
        </CopyToClipboard>
        <ExpandButton isSectionExpanded={isExpanded} onClick={handleExpandButtonClick} data-test="expand-embed" />
      </ButtonsWrapper>
    </CollapsibleSection>
  );
};
